/**
 * The sky from the ground (ruling 22): inside a planet's atmosphere the sky is sky, and distance hazes.
 * The game only ever drew an atmosphere as a rim seen from space, so at layer 0 the sky behind a
 * soldier was black space. Now, as the camera goes below a planet's atmosphere rim (1.07 R):
 *
 *   - (heights are measured above the ground under the camera, not the planet's sphere)
 *   - a sky dome around the camera fades in, in the planet's own atmosphere colour (its rim colour,
 *     lightened), as strong as its atmosphere (a thin one leaves a darker sky);
 *   - the stars and nebula fade out by as much;
 *   - distance hazes into the sky colour (scene fog) on the planet you are on only — the other
 *     planets in the sky stay visible, because that is where the shots come from. The haze runs from
 *     about half your horizon to four horizons, so it follows the person or the vehicle alike.
 *
 * The sky follows the sun: where the game's sun is up it is the day sky, where it is down the dome
 * darkens and the stars come back, with dusk between (the sun is the scene's main directional light).
 *
 * Above the atmosphere (the turn game's orbit view) all of it is off: nothing changes there.
 *
 * 67.40 (decision 140: simple and readable, not simulated) — the sky is a stand-in per world. On an air world the dome
 * is a gradient, horizon to zenith, warmer toward the sun's side and warmest at dusk (the coolest skies take the warmest
 * gradients, per the owner), darkening and thinning at night so the stars come through. A tiny world (the smallest size
 * class) holds no air: no dome, no haze — cool bare black, sharp stars, hard light — and whatever else stands in its sky
 * (a neighbour, a ring, a giant: they are the scene's own lit meshes) shows plain. One sun direction (the scene's main
 * light) lights every planet the same way. One dome, no lights; the terrain palette untouched.
 * Presentation only.
 */
import * as THREE from 'three';
import type { PlanetState, PlanetArchetype } from '../types';
import { ARCHETYPE_GLOW, SIZE_CATEGORY_RADIUS } from '../constants';
import type { PlanetMeshData } from './renderer';
import { getSurfaceRadius } from '../core/surface';
import { fogForSight } from './weatherView';

/** The weather's hand on the sky (rendering/weatherView.ts): a tint and how far toward it, and the sight range (world units; Infinity = clear). */
export interface SkyWeather { tint: THREE.Color; tintAmount: number; sight: number }

/** A material that takes scene fog (Three's base Material type does not declare the flag). */
type FogMaterial = THREE.Material & { fog?: boolean };

/** The atmosphere rim's radius, as a fraction of the planet's (renderer.ts draws it at 1.07 R). */
export const ATMOSPHERE_TOP = 1.07;

/** How far inside a planet's atmosphere the camera is: 0 at the rim and above, 1 on the ground. */
export function insideAtmosphere(altitude: number, radius: number): number {
  const depth = radius * (ATMOSPHERE_TOP - 1);
  return Math.max(0, Math.min(1, 1 - altitude / depth));
}

/**
 * The sky by planet type, seen from the ground: a colour and how thick the air is (the dome's opacity
 * on the ground). Temperate and tidal worlds get a blue sky, cold ones a pale one, volcanic ash, arid
 * amber dust, fungal a violet haze, sonora lavender; overgrowth a green haze.
 */
export const SKIES: Partial<Record<PlanetArchetype | 'earth', { color: string; strength: number }>> = {
  thalia: { color: '#9cc4de', strength: 0.78 }, earth: { color: '#9cc4de', strength: 0.78 },
  pelago: { color: '#92c2d6', strength: 0.78 }, bruma: { color: '#c4d2e2', strength: 0.72 },
  viridis: { color: '#a8d2b4', strength: 0.72 }, murex: { color: '#b6a6cc', strength: 0.62 },
  sonora: { color: '#c4b4dc', strength: 0.62 }, ochrus: { color: '#dcbc94', strength: 0.58 },
  tephra: { color: '#a89a94', strength: 0.58 }, pallor: { color: '#cfc6ae', strength: 0.5 },
};

/** A planet's sky: from the table by its palette (earth) or archetype; otherwise its atmosphere rim colour, lightened. */
export function skyOf(planet: Pick<PlanetState, 'archetype' | 'palette'>): { color: THREE.Color; strength: number } {
  const t = (planet.palette?.name === 'earth' ? SKIES.earth : undefined) ?? SKIES[planet.archetype as PlanetArchetype];
  if (t) return { color: new THREE.Color(t.color), strength: t.strength };
  const glow = ARCHETYPE_GLOW[planet.archetype as PlanetArchetype];
  const base = glow ? new THREE.Color(glow.color) : new THREE.Color(planet.palette.a).lerp(new THREE.Color(planet.palette.b), 0.5);
  const opacity = glow ? glow.opacity : 0.04;
  return { color: base.lerp(new THREE.Color('#ffffff'), 0.4), strength: Math.min(0.9, Math.max(0.3, opacity * 8)) };
}

/**
 * 67.40: each air world's sky gradient — the zenith's colour, the horizon's, the warm colour toward the sun, and how far
 * the sun's side warms by day (dusk warms it further). The coolest skies get the warmest gradients (the owner); an amber
 * or ash sky, already warm, gets less. The dome's thickness is SKIES' strength.
 */
export interface SkyLook { zenith: THREE.Color; horizon: THREE.Color; warm: THREE.Color; warmth: number; strength: number }
export const SKY_LOOKS: Partial<Record<PlanetArchetype | 'earth', { zenith: string; horizon: string; warm: string; warmth: number }>> = {
  thalia: { zenith: '#4f8cc8', horizon: '#bcd8ec', warm: '#f2c08a', warmth: 0.55 }, earth: { zenith: '#4f8cc8', horizon: '#bcd8ec', warm: '#f2c08a', warmth: 0.55 },
  pelago: { zenith: '#3e86b4', horizon: '#b2d8e2', warm: '#f0c492', warmth: 0.6 },
  bruma: { zenith: '#7e9cc6', horizon: '#dce6f0', warm: '#f6b88a', warmth: 0.75 },
  viridis: { zenith: '#5e9e86', horizon: '#c4e2c8', warm: '#ecd08c', warmth: 0.45 },
  murex: { zenith: '#5a4a86', horizon: '#c4b0d8', warm: '#f0a090', warmth: 0.6 },
  sonora: { zenith: '#7466a8', horizon: '#d6cce8', warm: '#f4b4a0', warmth: 0.6 },
  ochrus: { zenith: '#b08a5e', horizon: '#ecd2ac', warm: '#ffd090', warmth: 0.25 },
  tephra: { zenith: '#6e6460', horizon: '#bcaea6', warm: '#e89060', warmth: 0.4 },
  pallor: { zenith: '#a8a294', horizon: '#e6dece', warm: '#f4d0a0', warmth: 0.35 },
};

/** 67.40: a world too small to hold air (the tiny size class) — its sky is bare space. */
export function airless(planet: Pick<PlanetState, 'radius'>): boolean {
  return planet.radius < SIZE_CATEGORY_RADIUS.tiny[1];
}

/** 67.40: a world's sky gradient, or null when it holds no air. Unlisted skies derive from skyOf's colour. */
export function skyLook(planet: Pick<PlanetState, 'archetype' | 'palette' | 'radius'>): SkyLook | null {
  if (airless(planet)) return null;
  const base = skyOf(planet);
  const t = (planet.palette?.name === 'earth' ? SKY_LOOKS.earth : undefined) ?? SKY_LOOKS[planet.archetype as PlanetArchetype];
  if (t) return { zenith: new THREE.Color(t.zenith), horizon: new THREE.Color(t.horizon), warm: new THREE.Color(t.warm), warmth: t.warmth, strength: base.strength };
  return { zenith: base.color.clone().multiplyScalar(0.7), horizon: base.color.clone().lerp(new THREE.Color('#ffffff'), 0.35), warm: new THREE.Color('#f2c08a'), warmth: 0.5, strength: base.strength };
}

/** 67.40: how much of dusk it is where the sun stands `elevation` — 1 with the sun on the horizon, gone well above or below it. */
export function dusk(elevation: number): number {
  const x = (elevation - 0.02) / 0.16;
  return Math.exp(-x * x);
}

/**
 * 67.40: the sky dome's material — a gradient by the view's height over the local horizon, warming toward the sun's
 * side (more at dusk), dimmed by night; its uniforms set by `setSkyDome`. Back faces of a sphere round the camera.
 */
export function skyDomeMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uZenith: { value: new THREE.Color() }, uHorizon: { value: new THREE.Color() }, uWarm: { value: new THREE.Color() }, uTint: { value: new THREE.Color() },
      uUp: { value: new THREE.Vector3(0, 1, 0) }, uSun: { value: new THREE.Vector3(0, 1, 0) },
      uWarmth: { value: 0 }, uDay: { value: 1 }, uDusk: { value: 0 }, uTintAmount: { value: 0 }, uOpacity: { value: 0 },
    },
    vertexShader: `varying vec3 vWorld;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vWorld = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: `uniform vec3 uZenith, uHorizon, uWarm, uTint, uUp, uSun; uniform float uWarmth, uDay, uDusk, uTintAmount, uOpacity;
      varying vec3 vWorld;
      void main() {
        vec3 dir = normalize(vWorld - cameraPosition);
        float h = dot(dir, uUp), t = smoothstep(0.0, 0.65, max(h, 0.0));
        vec3 col = mix(uHorizon, uZenith, pow(t, 0.7));
        vec3 flatDir = dir - uUp * h, flatSun = uSun - uUp * dot(uSun, uUp);
        float side = (length(flatDir) > 1e-4 && length(flatSun) > 1e-4) ? max(0.0, dot(normalize(flatDir), normalize(flatSun))) : 0.0;
        float low = 1.0 - t * 0.8;
        col = mix(col, uWarm, clamp(pow(side, 5.0) * low * uWarmth * uDay * 0.6, 0.0, 1.0));   // by day, a gentle warmth on the sun's side
        col = mix(col, uTint, uTintAmount);                               // the weather's hand (weatherView)
        col *= 0.07 + 0.93 * uDay;                                       // night
        col += uWarm * uDusk * (0.6 * pow(side, 1.6) * low + 0.14 * (1.0 - t)) * (1.0 - 0.7 * uTintAmount);   // dusk: the sun's side glows, the horizon all round a little
        gl_FragColor = vec4(col, uOpacity);
      }`,
    transparent: true, depthWrite: false, side: THREE.BackSide, fog: false,
  });
}

/** 67.40: set the dome for a look, the local up, the sun's direction, and the weather's tint (0 = none). */
export function setSkyDome(mat: THREE.ShaderMaterial, look: SkyLook, up: THREE.Vector3, sun: THREE.Vector3, opacity: number, tint: THREE.Color | null = null, tintAmount = 0): void {
  const u = mat.uniforms, el = up.dot(sun);
  u.uZenith.value.copy(look.zenith); u.uHorizon.value.copy(look.horizon); u.uWarm.value.copy(look.warm);
  u.uUp.value.copy(up); u.uSun.value.copy(sun);
  u.uWarmth.value = look.warmth; u.uDay.value = daylight(el); u.uDusk.value = dusk(el); u.uOpacity.value = opacity;
  if (tint) u.uTint.value.copy(tint);
  u.uTintAmount.value = tintAmount;
}

/** How much day it is where the sun stands `elevation` (the sine of its height over the local horizon): 0 at night, 1 by day, a smooth dusk from just below the horizon to a little above. */
export function daylight(elevation: number): number {
  const d = Math.max(0, Math.min(1, (elevation + 0.12) / 0.4));
  return d * d * (3 - 2 * d);
}

export class SkyHaze {
  private readonly dome: THREE.Mesh;
  private readonly domeMat: THREE.ShaderMaterial;
  private readonly fog: THREE.Fog;
  private skyLayers: Array<{ mat: FogMaterial & { opacity: number }; base: number }> = [];
  private hazed: number | null = null;   // the planet whose terrain takes the fog
  private readonly sunDir = new THREE.Vector3(40, 50, 30).normalize();
  /** The direction to the sun (67.73: the far field's lamps read night where each stands). */
  get sun(): THREE.Vector3 { return this.sunDir; }
  /** 67.30: how far the stars show this frame (1 in space or at night; 0 under a full day sky), and the local up inside an atmosphere (null in open space) — for the shooting stars. */
  starsShown = 1;
  private readonly upAt = new THREE.Vector3();
  localUp: THREE.Vector3 | null = null;

  constructor(private readonly scene: THREE.Scene) {
    this.domeMat = skyDomeMaterial();   // 67.40: the gradient
    this.dome = new THREE.Mesh(new THREE.SphereGeometry(200, 32, 16), this.domeMat);
    this.dome.renderOrder = -1;          // first among the see-through layers: everything else draws over the sky
    this.dome.frustumCulled = false;
    this.dome.raycast = () => {};
    this.dome.visible = false;
    scene.add(this.dome);
    this.fog = new THREE.Fog(0x9ec4dc, 1e4, 1e5);
    scene.fog = this.fog;
    // the sun: the brightest directional light (renderer.ts createScene)
    let sun: THREE.DirectionalLight | null = null;
    scene.traverse(o => { const l = o as THREE.DirectionalLight; if (l.isDirectionalLight && (!sun || l.intensity > sun.intensity)) sun = l; });
    if (sun) this.sunDir.copy((sun as THREE.DirectionalLight).position).normalize();
    scene.traverse(o => {
      const m = (o as THREE.Mesh).material as (FogMaterial & { opacity: number }) | undefined;
      if (o.userData.skyLayer && m) { m.fog = false; this.skyLayers.push({ mat: m, base: m.opacity }); }
    });
  }

  /** Fit the sky to the camera: the planet nearest it, how deep in its air, its colour. */
  update(camera: THREE.Camera, planets: readonly PlanetState[], meshes: Map<number, PlanetMeshData>, weather: SkyWeather | null = null): void {
    const cam = camera.position;
    // the planet nearest the camera, and the camera's height above the ground under it (terrain can stand well above the sphere)
    let best: PlanetState | null = null, gap = Infinity;
    for (const p of planets) {
      if (p.type === 'star' || p.type === 'wormhole' || p.alive === false) continue;
      const a = Math.hypot(cam.x - p.position.x, cam.y - p.position.y, cam.z - p.position.z) - p.radius;
      if (a < gap) { gap = a; best = p; }
    }
    let alt = gap;
    if (best && gap < best.radius) {
      const d = { x: cam.x - best.position.x, y: cam.y - best.position.y, z: cam.z - best.position.z };
      alt = Math.max(0, Math.hypot(d.x, d.y, d.z) - getSurfaceRadius(best, d));
    }
    const inside = best ? insideAtmosphere(alt, best.radius) : 0;
    const look = best ? skyLook(best) : null;
    if (!best || inside <= 0 || !look) { this.off(meshes); return; }   // 67.40: a tiny world holds no air — bare space
    // daylight: the sun's height over the local horizon — day sky above, night (and the stars) below, dusk between
    const up = new THREE.Vector3(cam.x - best.position.x, cam.y - best.position.y, cam.z - best.position.z).normalize();
    const daySmooth = daylight(up.dot(this.sunDir));
    // the haze takes the horizon's colour (what the far ground fades into), dimmed by night and tinted by the weather
    const shown = look.horizon.clone().multiplyScalar(0.07 + 0.93 * daySmooth);
    const tintAmount = weather && weather.tintAmount > 0 ? weather.tintAmount * inside : 0;
    if (weather && tintAmount > 0) shown.lerp(weather.tint.clone().multiplyScalar(0.1 + 0.9 * daySmooth), tintAmount);   // the weather's tint, inside the air
    const veil = Math.pow(inside, 0.7) * (0.35 + 0.65 * daySmooth);   // at night the air is thinner to the eye: the stars show
    this.dome.visible = true;
    this.dome.position.copy(cam);
    setSkyDome(this.domeMat, look, up, this.sunDir, look.strength * veil, weather?.tint ?? null, tintAmount);
    for (const l of this.skyLayers) l.mat.opacity = l.base * (1 - veil);
    this.starsShown = 1 - veil; this.localUp = this.upAt.copy(up);
    // haze: from half the horizon to four horizons, as deep as the air (the horizon from height h is √(2Rh))
    const horizon = Math.sqrt(2 * best.radius * Math.max(1e-4, alt));
    this.fog.color.copy(shown);
    this.fog.near = 0.5 * horizon / inside;
    this.fog.far = 4 * horizon / Math.max(0.05, inside * look.strength);
    // weather: the fog closes to the sight range (what the sim cannot see, you cannot either), never opening past the clear air's
    if (weather && Number.isFinite(weather.sight)) {
      const band = fogForSight(weather.sight);
      if (band) { this.fog.near = Math.min(this.fog.near, band.near); this.fog.far = Math.min(this.fog.far, band.far); }
    }
    this.hazeOnly(best.id, meshes);
  }

  /** Only the planet you are on takes the fog; the others in the sky stay clear. */
  private hazeOnly(planetId: number | null, meshes: Map<number, PlanetMeshData>): void {
    if (this.hazed === planetId) return;
    for (const [id, m] of meshes) {
      const mat = m.mesh.material as FogMaterial;
      const want = id === planetId;
      if (mat.fog !== want) { mat.fog = want; mat.needsUpdate = true; }
      if (m.glow) (m.glow.material as FogMaterial).fog = false;
    }
    this.hazed = planetId;
  }

  private off(meshes: Map<number, PlanetMeshData>): void {
    this.starsShown = 1; this.localUp = null;
    if (!this.dome.visible && this.hazed === null) return;
    this.dome.visible = false;
    for (const l of this.skyLayers) l.mat.opacity = l.base;
    this.fog.near = 1e4; this.fog.far = 1e5;
    this.hazeOnly(null, meshes);
  }

  dispose(): void {
    this.scene.remove(this.dome);
    this.dome.geometry.dispose(); this.domeMat.dispose();
    if (this.scene.fog === this.fog) this.scene.fog = null;
    for (const l of this.skyLayers) l.mat.opacity = l.base;
  }
}
